// ─── Types ───────────────────────────────────────────────────────────────────

export interface KIStep {
  id: string;
  label: string;
  loc: string;
}

export interface DropItem {
  name: string;
  sell?: string; // "sell as" name for Pulling the Plug visions
}

export interface DropPool {
  label: string;
  type: string; // "0-1" | "1-of"
  items: DropItem[];
}

export interface ENM {
  id: string;
  name: string;
  level: number;
  zone: string;
  cdGroup: string;
  exp: number;
  kiName: string;
  // When does the 5-day cooldown timer start?
  //   "acquisition" — when KI is obtained (final KI step checked)
  //   "run"         — when the fight is logged via logRun
  // Pulling the Plug, Fire in the Sky: NPC hands you the KI and won't
  // hand you another for 5 days → "acquisition".
  // Holy Cow, Sheep in Antlion's Clothing: cooldown begins when the
  // fight is completed → "run".
  cooldownTrigger: "acquisition" | "run";
  // Dashboard grouping: "promyvion" ENMs render inside the collapsible
  // Promyvion shelf at the bottom of the roster instead of the main list.
  section?: "promyvion";
  kiSteps: KIStep[];
  dropPools: DropPool[];
}

export interface Drop {
  id: string;
  enm_id: string;
  run_date: string;
  items: string[];
  run_number: number;
}

export interface Sale {
  id: string;
  enm_id: string;
  item_name: string;
  price: number;
  sale_date: string;
  seller: string;
  source?: "manual" | "cron" | "sync";
}

export interface KIState {
  steps: Record<string, boolean>;
  acquiredAt: string | null;
  needsRun: boolean;
}

export interface PriceEntry {
  price: number;
  updated: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

export const PLAYERS = ["Jezza", "Donpeyote"] as const;
export type Player = (typeof PLAYERS)[number];
export const CD_MS = 5 * 24 * 60 * 60 * 1000; // 5 days

// The eight memory clusters shared by every censer ENM (Pulling the Plug and
// the three 30-cap spires). Not crystal clusters — the HXI API knows them as
// "cluster of <adj> memories"; see the slug notes in cron/index.ts.
const MEMORY_CLUSTERS: DropItem[] = [
  { name: "Bitter Cluster" }, { name: "Burning Cluster" }, { name: "Fleeting Cluster" },
  { name: "Malevolent Cluster" }, { name: "Profane Cluster" }, { name: "Radiant Cluster" },
  { name: "Somber Cluster" }, { name: "Startling Cluster" },
];

export const ENM_DATA: ENM[] = [
  {
    id: "test-your-mite", name: "Test Your Mite", level: 40,
    zone: "The Shrouded Maw", cdGroup: "shrouded-maw", exp: 3500,
    kiName: "Astral Covenant",
    cooldownTrigger: "acquisition", // TODO: confirm — defaulted to match other NPC-trade ENMs
    kiSteps: [
      { id: "stone", label: "Farm Florid Stone", loc: "Pso'Xja" },
      { id: "trade", label: "Trade to Ghebi Damomohe", loc: "Lower Jeuno I-7" },
    ],
    dropPools: [
      { label: "Pool 1", type: "0-1", items: [{ name: "Cloud Evoker" }] },
      { label: "Pool 2", type: "0-1", items: [{ name: "Geist Earring" }, { name: "Quick Belt" }] },
      { label: "Pool 3", type: "0-1", items: [{ name: "Crossbowman's Ring" }, { name: "Woodsman Ring" }, { name: "Ether Ring" }] },
    ],
  },
  {
    id: "fire-in-the-sky", name: "Fire in the Sky", level: 40,
    zone: "Monarch Linn", cdGroup: "monarch-linn", exp: 2500,
    kiName: "Monarch Beard",
    cooldownTrigger: "acquisition",
    kiSteps: [
      { id: "talk", label: "Talk to Morangeart", loc: "Tavnazian Safehold G-10" },
    ],
    dropPools: [
      { label: "Pool 1", type: "0-1", items: [{ name: "Cloud Evoker" }] },
      { label: "Pool 2", type: "0-1", items: [{ name: "Thug's Zamburak" }, { name: "Horror Voulge" }] },
      { label: "Pool 3", type: "0-1", items: [{ name: "Crossbowman's Ring" }, { name: "Woodsman Ring" }, { name: "Ether Ring" }] },
    ],
  },
  {
    id: "pulling-the-plug", name: "Pulling the Plug", level: 50,
    zone: "Spire of Vahzl", cdGroup: "spire-vahzl", exp: 3000,
    kiName: "Censer of Acrimony",
    cooldownTrigger: "acquisition",
    kiSteps: [
      { id: "talk", label: "Talk to Venessa", loc: "Ru'Lude Gardens H-10" },
    ],
    dropPools: [
      { label: "Clusters", type: "0-1", items: MEMORY_CLUSTERS },
      { label: "Visions A", type: "1-of", items: [
        { name: "Beatific Image (Tiger Eye)", sell: "Tiger Eye" },
        { name: "Grave Image (Habu Skin)", sell: "Habu Skin" },
        { name: "Valorous Image (Rheiyoh Leather)", sell: "Rheiyoh Leather" },
      ]},
      { label: "Visions B", type: "1-of", items: [
        { name: "Ancient Image (Oversized Fang)", sell: "Oversized Fang" },
        { name: "Virgin Image (Super Cermet)", sell: "Super Cermet" },
      ]},
      { label: "Rare", type: "0-1", items: [
        { name: "Impetuous Vision (Toreador's Ring)", sell: "Toreador's Ring" },
        { name: "Snide Vision (Safety Mantle)", sell: "Safety Mantle" },
        { name: "Tenuous Vision (Astral Rope)", sell: "Astral Rope" },
      ]},
    ],
  },
  {
    id: "sheep-in-antlions-clothing", name: "Sheep in Antlion's Clothing", level: 75,
    zone: "Boneyard Gully", cdGroup: "boneyard-gully", exp: 2500,
    kiName: "Miasma Filter",
    cooldownTrigger: "run",
    kiSteps: [
      { id: "pouch", label: "Get Pouch", loc: "Attohwa Chasm" },
      { id: "fill", label: "Fill Pouch", loc: "Attohwa Chasm" },
      { id: "trade", label: "Trade for Miasma Filter", loc: "Attohwa Chasm" },
    ],
    dropPools: [
      { label: "Materials", type: "0-1", items: [{ name: "Galateia" }, { name: "Kejusu Satin" }, { name: "Viridian Urushi" }] },
      { label: "Evoker", type: "0-1", items: [{ name: "Cloud Evoker" }] },
      { label: "Equip 1", type: "0-1", items: [
        { name: "Hagun" }, { name: "Martial Axe" }, { name: "Martial Wand" },
        { name: "Forager's Mantle" }, { name: "Harmonia's Torque" },
      ]},
      { label: "Equip 2", type: "0-1", items: [
        { name: "Hagun" }, { name: "Martial Axe" }, { name: "Martial Wand" },
        { name: "Forager's Mantle" }, { name: "Harmonia's Torque" },
      ]},
    ],
  },
  // Holy Cow and Brothers share the bearclaw-pinnacle group: one Zephyr Fan,
  // one 5-day window, run either fight with it ("only one of the battlefields
  // can be completed every 5 Earth days" — horizonffxi.wiki). KI steps are
  // duplicated on both entries (same step ids) so either detail page renders
  // the shared group state.
  {
    id: "holy-cow", name: "Holy Cow", level: 75,
    zone: "Bearclaw Pinnacle", cdGroup: "bearclaw-pinnacle", exp: 4000,
    kiName: "Zephyr Fan",
    cooldownTrigger: "run",
    kiSteps: [
      { id: "pouch", label: "Get Cotton Pouch from Zebada", loc: "Uleguerand Range F-12" },
      { id: "fill", label: "Fill at Chamneat Spring", loc: "Uleguerand Range F-8" },
      { id: "trade", label: "Trade Chamnaet Ice to Zebada", loc: "Uleguerand Range F-12" },
    ],
    dropPools: [
      { label: "Materials", type: "0-1", items: [{ name: "Dragon Bone" }, { name: "Eltoro Leather" }, { name: "Cassia Lumber" }] },
      { label: "Evoker", type: "0-1", items: [{ name: "Cloud Evoker" }] },
      { label: "Equip 1", type: "0-1", items: [
        { name: "Martial Bow" }, { name: "Martial Knuckles" }, { name: "Gigant Mantle" },
        { name: "Psilos Mantle" }, { name: "Scroll of Raise III" },
      ]},
      { label: "Equip 2", type: "0-1", items: [
        { name: "Martial Bow" }, { name: "Martial Knuckles" }, { name: "Gigant Mantle" },
        { name: "Psilos Mantle" }, { name: "Scroll of Raise III" },
      ]},
    ],
  },
  {
    id: "brothers", name: "Brothers", level: 75,
    zone: "Bearclaw Pinnacle", cdGroup: "bearclaw-pinnacle", exp: 3500,
    kiName: "Zephyr Fan",
    cooldownTrigger: "run",
    kiSteps: [
      { id: "pouch", label: "Get Cotton Pouch from Zebada", loc: "Uleguerand Range F-12" },
      { id: "fill", label: "Fill at Chamneat Spring", loc: "Uleguerand Range F-8" },
      { id: "trade", label: "Trade Chamnaet Ice to Zebada", loc: "Uleguerand Range F-12" },
    ],
    dropPools: [
      { label: "Materials", type: "0-1", items: [{ name: "Dragon Bone" }, { name: "Eltoro Leather" }, { name: "Cassia Lumber" }] },
      { label: "Evoker", type: "0-1", items: [{ name: "Cloud Evoker" }] },
      { label: "Equip 1", type: "0-1", items: [
        { name: "Scouter's Rope" }, { name: "Hedgehog Bomb" }, { name: "Martial Anelace" },
        { name: "Martial Lance" }, { name: "Scroll of Raise III" },
      ]},
      { label: "Equip 2", type: "0-1", items: [
        { name: "Scouter's Rope" }, { name: "Hedgehog Bomb" }, { name: "Martial Anelace" },
        { name: "Martial Lance" }, { name: "Scroll of Raise III" },
      ]},
    ],
  },
  {
    id: "bionic-bug", name: "Bionic Bug", level: 75,
    zone: "Mine Shaft #2716", cdGroup: "mine-shaft", exp: 3500,
    kiName: "Shaft 2716 Operating Lever",
    cooldownTrigger: "acquisition", // TODO: confirm — defaulted to match other NPC-trade ENMs
    kiSteps: [
      { id: "stone", label: "Farm Sylvan Stone", loc: "Oldton Movalpolos" },
      { id: "trade", label: "Trade to Twinkbrix", loc: "Oldton Movalpolos E-13" },
    ],
    dropPools: [
      { label: "Materials", type: "1-of", items: [{ name: "Eltoro Leather" }, { name: "Cassia Lumber" }, { name: "Dragon Bone" }] },
      { label: "Evoker", type: "0-1", items: [{ name: "Cloud Evoker" }] },
      { label: "Equip 1", type: "0-1", items: [
        { name: "Martial Knife" }, { name: "Martial Scythe" }, { name: "Commander's Cape" },
        { name: "Faerie Hairpin" }, { name: "Scroll of Raise III" },
      ]},
      { label: "Equip 2", type: "0-1", items: [
        { name: "Martial Knife" }, { name: "Martial Scythe" }, { name: "Commander's Cape" },
        { name: "Faerie Hairpin" }, { name: "Scroll of Raise III" },
      ]},
    ],
  },
  // The three 30-cap Promyvion spire ENMs — collapsible "promyvion" shelf at
  // the bottom of the roster. Same censer flow as Pulling the Plug: Venessa
  // hands out the KI and won't hand out that censer again for 5 days →
  // "acquisition". Each spire's censer and timer is independent.
  // Data: horizonffxi.wiki/Playing_Host, /Simulant, /You_Are_What_You_Eat.
  {
    id: "playing-host", name: "Playing Host", level: 30,
    zone: "Spire of Mea", cdGroup: "spire-mea", exp: 3000,
    kiName: "Censer of Animus",
    cooldownTrigger: "acquisition",
    section: "promyvion",
    kiSteps: [
      { id: "talk", label: "Talk to Venessa", loc: "Ru'Lude Gardens H-10" },
    ],
    dropPools: [
      { label: "Clusters 1", type: "0-1", items: MEMORY_CLUSTERS },
      { label: "Clusters 2", type: "0-1", items: MEMORY_CLUSTERS },
      { label: "Clusters 3", type: "0-1", items: MEMORY_CLUSTERS },
      { label: "Visions", type: "0-1", items: [
        { name: "Solemn Vision (Guarding Earring)", sell: "Guarding Earring" },
        { name: "Valiant Vision (Augmenting Earring)", sell: "Augmenting Earring" },
        { name: "Pretentious Vision (Elemental Earring)", sell: "Elemental Earring" },
        { name: "Malicious Vision (Ninjutsu Earring)", sell: "Ninjutsu Earring" },
        { name: "Pristine Vision (Wind Earring)", sell: "Wind Earring" },
      ]},
    ],
  },
  {
    id: "simulant", name: "Simulant", level: 30,
    zone: "Spire of Holla", cdGroup: "spire-holla", exp: 3000,
    kiName: "Censer of Abandonment",
    cooldownTrigger: "acquisition",
    section: "promyvion",
    kiSteps: [
      { id: "talk", label: "Talk to Venessa", loc: "Ru'Lude Gardens H-10" },
    ],
    dropPools: [
      { label: "Clusters 1", type: "0-1", items: MEMORY_CLUSTERS },
      { label: "Clusters 2", type: "0-1", items: MEMORY_CLUSTERS },
      { label: "Clusters 3", type: "0-1", items: MEMORY_CLUSTERS },
      { label: "Visions", type: "0-1", items: [
        { name: "Vernal Vision (Evasion Earring)", sell: "Evasion Earring" },
        { name: "Punctilious Vision (Parrying Earring)", sell: "Parrying Earring" },
        { name: "Audacious Vision (Divine Earring)", sell: "Divine Earring" },
        { name: "Vivid Vision (Healing Earring)", sell: "Healing Earring" },
        { name: "Endearing Vision (Singing Earring)", sell: "Singing Earring" },
      ]},
    ],
  },
  {
    id: "you-are-what-you-eat", name: "You Are What You Eat", level: 30,
    zone: "Spire of Dem", cdGroup: "spire-dem", exp: 3000,
    kiName: "Censer of Antipathy",
    cooldownTrigger: "acquisition",
    section: "promyvion",
    kiSteps: [
      { id: "talk", label: "Talk to Venessa", loc: "Ru'Lude Gardens H-10" },
    ],
    dropPools: [
      { label: "Clusters 1", type: "0-1", items: MEMORY_CLUSTERS },
      { label: "Clusters 2", type: "0-1", items: MEMORY_CLUSTERS },
      { label: "Clusters 3", type: "0-1", items: MEMORY_CLUSTERS },
      { label: "Visions", type: "0-1", items: [
        { name: "Violent Vision (Buckler Earring)", sell: "Buckler Earring" },
        { name: "Painful Vision (Dark Earring)", sell: "Dark Earring" },
        { name: "Timorous Vision (Enfeebling Earring)", sell: "Enfeebling Earring" },
        { name: "Brilliant Vision (Summoning Earring)", sell: "Summoning Earring" },
        { name: "Venerable Vision (String Earring)", sell: "String Earring" },
      ]},
    ],
  },
];

// ─── Cooldown groups ─────────────────────────────────────────────────────────
//
// KI state (steps, holding, 5-day countdown) lives per cooldown group, not per
// ENM — grouped ENMs (Holy Cow / Brothers) share one KI and one window. Rows
// in D1's ki_state key on this value.

export const kiKey = (enm: ENM) => enm.cdGroup;

export interface CDGroup {
  key: string;
  enms: ENM[];
  /** "Holy Cow / Brothers" for shared groups, plain name otherwise. */
  label: string;
  kiName: string;
}

export const CD_GROUPS: CDGroup[] = (() => {
  const byKey = new Map<string, ENM[]>();
  for (const e of ENM_DATA) {
    const list = byKey.get(e.cdGroup) ?? [];
    list.push(e);
    byKey.set(e.cdGroup, list);
  }
  return [...byKey.entries()].map(([key, enms]) => ({
    key,
    enms,
    label: enms.map(e => e.name).join(" / "),
    kiName: enms[0].kiName,
  }));
})();

// ─── Formatters ──────────────────────────────────────────────────────────────

export const fmtGil = (n: number) => (n ? n.toLocaleString() + " gil" : "—");

export const fmtGilShort = (n: number) => {
  if (!n) return "—";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(n >= 1e4 ? 0 : 1) + "k";
  return n + "";
};

export const fmtDate = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
    " " +
    d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
  );
};

export const genId = () => Math.random().toString(36).slice(2, 10);
